<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import type { MarketFlowData, VisualizationConfig } from '../types/market'
import type { PlaybackStatus } from './FundsFlowCanvas.vue'
import type { VideoJob, VideoJobMode } from '../services/VideoGenerationService'

const props = defineProps<{
  data: MarketFlowData
  config: VisualizationConfig
  status: PlaybackStatus
  videoJob: VideoJob | null
  videoError: string
}>()

const emit = defineEmits<{
  play: []
  pause: []
  restart: []
  previous: []
  next: []
  seek: [milliseconds: number]
  seekFrame: [index: number]
  speed: [value: number]
  generateVideo: [mode: VideoJobMode, targetDurationSeconds: number]
  openOutput: []
  playVideo: []
}>()

const hidden = ref(false)
const targetDurationSeconds = ref(16.5)
const generationBusy = computed(() => props.videoJob != null && ['preparing', 'rendering', 'encoding'].includes(props.videoJob.status))
const scrubValue = computed({
  get: () => props.status.elapsedMs,
  set: (value: number) => emit('seek', Number(value)),
})

function onKeydown(event: KeyboardEvent): void {
  if (event.key.toLowerCase() === 'h') hidden.value = !hidden.value
  if (event.code === 'Space' && !(event.target instanceof HTMLInputElement)) {
    event.preventDefault()
    if (props.status.playing) emit('pause')
    else emit('play')
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <button v-if="hidden" class="show-controls" type="button" @click="hidden = false">显示控制台 · H</button>
  <aside v-else class="controls" aria-label="开发模式控制面板">
    <div class="controls__header">
      <div>
        <strong>导演控制台</strong>
        <span>{{ status.time }} · 第 {{ status.frameIndex + 1 }} / {{ data.frames.length }} 帧</span>
      </div>
      <button type="button" title="隐藏控制台（H）" @click="hidden = true">×</button>
    </div>

    <div class="transport">
      <button type="button" @click="emit('restart')">↺</button>
      <button type="button" @click="emit('previous')">‹</button>
      <button v-if="!status.playing" class="transport__primary" type="button" @click="emit('play')">▶</button>
      <button v-else class="transport__primary" type="button" @click="emit('pause')">Ⅱ</button>
      <button type="button" @click="emit('next')">›</button>
    </div>

    <label class="scrubber">
      <input v-model.number="scrubValue" type="range" min="0" :max="status.durationMs" step="10" />
      <span>{{ (status.elapsedMs / 1000).toFixed(1) }}s / {{ (status.durationMs / 1000).toFixed(1) }}s</span>
    </label>

    <div class="speed-row">
      <span>播放速度</span>
      <button
        v-for="value in [0.5, 1, 2, 4]"
        :key="value"
        type="button"
        :class="{ active: status.speed === value }"
        @click="emit('speed', value)"
      >{{ value }}×</button>
    </div>

    <label class="jump-row">
      <span>跳转市场时间</span>
      <select :value="status.frameIndex" @change="emit('seekFrame', Number(($event.target as HTMLSelectElement).value))">
        <option v-for="(frame, index) in data.frames" :key="frame.time" :value="index">{{ frame.time }}</option>
      </select>
    </label>

    <section class="video-generation" data-testid="video-generation">
      <div class="video-generation__title">视频生成</div>
      <label class="video-duration">
        <span>成片时长</span>
        <select v-model.number="targetDurationSeconds" :disabled="generationBusy" data-testid="video-duration-select">
          <option :value="15">15 秒</option>
          <option :value="16.5">16.5 秒</option>
          <option :value="18">18 秒</option>
        </select>
        <small>保持 60 FPS · 连续插值</small>
      </label>
      <div class="video-generation__option">
        <div><strong>午盘视频</strong><span>09:30 - 11:30</span></div>
        <button data-testid="generate-noon-video" type="button" :disabled="generationBusy" @click="emit('generateVideo', 'noon', targetDurationSeconds)">生成午盘视频</button>
      </div>
      <div class="video-generation__option">
        <div><strong>全天视频</strong><span>09:30 - 11:30 · 13:00 - 15:00</span></div>
        <button data-testid="generate-close-video" type="button" :disabled="generationBusy" @click="emit('generateVideo', 'close', targetDurationSeconds)">生成全天视频</button>
      </div>

      <div v-if="videoJob || videoError" class="generation-status" data-testid="video-generation-status" :class="{ 'generation-status--failed': videoError || videoJob?.status === 'failed' }">
        <div class="generation-status__row">
          <strong>{{ videoJob?.stage || '视频生成失败' }}</strong>
          <span v-if="videoJob">{{ videoJob.progress }}%</span>
        </div>
        <div v-if="videoJob && videoJob.totalFrames" class="generation-status__frames">
          {{ videoJob.currentFrame }} / {{ videoJob.totalFrames }} frames
        </div>
        <div v-if="videoJob && generationBusy" class="generation-progress"><i :style="{ width: `${videoJob.progress}%` }" /></div>
        <p v-if="videoError || videoJob?.error">{{ videoError || videoJob?.error }}</p>
        <p v-else-if="videoJob?.status === 'completed'">生成成功 · {{ videoJob.outputFile }}</p>
        <div v-if="videoJob?.status === 'completed'" class="generation-actions">
          <button type="button" @click="emit('openOutput')">打开输出目录</button>
          <button type="button" @click="emit('playVideo')">播放视频</button>
        </div>
      </div>
    </section>

    <details>
      <summary>视觉与物理参数</summary>
      <div class="parameter-grid">
        <label><span>minRadius</span><input v-model.number="config.minRadius" type="range" min="24" max="64" /></label>
        <label><span>maxRadius</span><input v-model.number="config.maxRadius" type="range" min="86" max="150" /></label>
        <label><span>collisionPadding</span><input v-model.number="config.collisionPadding" type="range" min="2" max="24" /></label>
        <label><span>animationDuration</span><input v-model.number="config.animationDuration" type="range" min="120" max="600" step="10" /></label>
        <label><span>glowIntensity</span><input v-model.number="config.glowIntensity" type="range" min="0" max="1.4" step="0.05" /></label>
        <label><span>particleIntensity</span><input v-model.number="config.particleIntensity" type="range" min="0" max="1" step="0.05" /></label>
        <label><span>Champion Effect</span><input v-model="config.championEffectEnabled" data-testid="champion-effect" type="checkbox" /></label>
        <label><span>Physics Debug</span><input v-model="config.physicsDebug" data-testid="physics-debug" type="checkbox" /></label>
      </div>
    </details>
    <p class="controls__hint">Space 播放/暂停 · H 隐藏面板 · 导出模式自动隐藏</p>
  </aside>
</template>

<style scoped>
.controls,
.show-controls {
  position: fixed;
  z-index: 10;
  color: var(--text-primary);
  font: 13px/1.35 Inter, "Microsoft YaHei", sans-serif;
}

.controls {
  top: 24px;
  right: 24px;
  width: 330px;
  padding: 17px;
  border: 1px solid rgb(161 182 194 / 16%);
  border-radius: 20px;
  background: rgb(10 15 19 / 88%);
  box-shadow: 0 24px 80px rgb(0 0 0 / 44%);
  backdrop-filter: blur(22px) saturate(115%);
}

.show-controls {
  top: 20px;
  right: 20px;
  border: 1px solid rgb(161 182 194 / 14%);
  border-radius: 999px;
  padding: 9px 14px;
  background: rgb(10 15 19 / 76%);
}

.controls__header,
.speed-row,
.jump-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 9px;
}

.controls__header strong,
.controls__header span {
  display: block;
}

.controls__header strong { font-size: 15px; }
.controls__header span { margin-top: 3px; color: var(--text-secondary); font-size: 11px; }
.controls__header button { font-size: 21px; }

button,
select {
  color: inherit;
  border: 1px solid rgb(163 182 193 / 14%);
  border-radius: 10px;
  background: rgb(255 255 255 / 5%);
  cursor: pointer;
}

button:hover,
button.active { border-color: rgb(209 222 229 / 30%); background: rgb(255 255 255 / 10%); }
button:disabled { opacity: 0.42; cursor: not-allowed; }

.transport {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 7px;
  margin: 16px 0 12px;
}

.transport button { height: 38px; }
.transport__primary { color: #fff; background: rgb(202 80 76 / 65%); }

.scrubber { display: grid; gap: 5px; color: var(--text-secondary); font-size: 11px; }
input[type="range"] { width: 100%; accent-color: #c65d58; }
.speed-row { margin: 14px 0; color: var(--text-secondary); }
.speed-row button { padding: 6px 10px; }
.jump-row select { min-width: 92px; padding: 7px 9px; }

.video-generation {
  display: grid;
  gap: 9px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgb(161 182 194 / 10%);
}

.video-generation__title { color: var(--text-secondary); font-size: 11px; letter-spacing: 1.4px; }
.video-duration { display: grid; grid-template-columns: auto 82px 1fr; align-items: center; gap: 8px; color: var(--text-secondary); font-size: 10px; }
.video-duration select { width: 82px; padding: 5px 7px; }
.video-duration small { color: rgb(178 194 203 / 42%); font-size: 9px; }
.video-generation__option { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.video-generation__option div { min-width: 0; }
.video-generation__option strong,
.video-generation__option span { display: block; }
.video-generation__option strong { font-size: 12px; font-weight: 600; }
.video-generation__option span { margin-top: 2px; color: var(--text-secondary); font-size: 9px; }
.video-generation__option button { flex: none; padding: 7px 10px; font-size: 11px; }

.generation-status { padding: 10px; border-radius: 11px; background: rgb(255 255 255 / 4%); }
.generation-status__row { display: flex; justify-content: space-between; gap: 10px; font-size: 11px; }
.generation-status__frames { margin-top: 4px; color: var(--text-secondary); font-size: 10px; }
.generation-progress { height: 4px; margin-top: 8px; overflow: hidden; border-radius: 999px; background: rgb(255 255 255 / 7%); }
.generation-progress i { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, var(--positive), #d88570); transition: width 180ms ease-out; }
.generation-status p { margin: 7px 0 0; color: var(--text-secondary); font-size: 10px; word-break: break-word; }
.generation-status--failed p { color: #dc7e74; }
.generation-actions { display: flex; gap: 7px; margin-top: 8px; }
.generation-actions button { padding: 6px 9px; font-size: 10px; }

details { margin-top: 13px; border-top: 1px solid rgb(161 182 194 / 10%); padding-top: 12px; }
summary { color: var(--text-secondary); cursor: pointer; }
.parameter-grid { display: grid; gap: 9px; margin-top: 12px; }
.parameter-grid label { display: grid; grid-template-columns: 120px 1fr; align-items: center; color: var(--text-secondary); font-size: 11px; }
.controls__hint { margin: 12px 0 0; color: rgb(178 194 203 / 42%); font-size: 10px; }
</style>
